import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { brokerCredentialsService, type MetaTraderPairingInfo, type MetaTraderStatus } from '@/services/brokerCredentials';

/****************
 * CONNECTING METATRADER. 2026-11-03.
 *
 * Nothing is typed in: CopyFi mints a pairing token, the person puts it and
 * CopyFi's address into the CopyFi Expert Advisor running in THEIR OWN
 * MetaTrader terminal, and the terminal starts talking to CopyFi. CopyFi never
 * sees their MetaTrader login or password. This panel creates the pairing,
 * shows the four steps with the two values to paste, and says when the terminal
 * has connected (backend api/metatrader.go).
 ****************/

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation('copy');
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-muted px-2 py-1 text-xs select-all">{value}</code>
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={label}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              /* the value stays selectable above */
            }
          }}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="sr-only">{copied ? t('action.copied') : t('action.copy')}</span>
        </Button>
      </div>
    </div>
  );
}

/* The assets the broker behind a terminal offers CopyFi, and why the others are not offered. */
function MetaTraderAssets({ assets }: { assets: NonNullable<MetaTraderStatus['assets']> }) {
  const { t } = useTranslation('copy');
  if (assets.offered.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('broker.mt.noneOffered')}</p>;
  }
  const names = assets.offered.slice(0, 40).map((a) => a.asset.replace(/USDT$/, ''));
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p>{t('broker.mt.offered', { count: assets.offered.length, list: names.join(', ') })}</p>
      {assets.not_offered_total > 0 && (
        <details>
          <summary className="cursor-pointer">{t('broker.mt.notOffered', { count: assets.not_offered_total })}</summary>
          <ul className="mt-1 max-h-48 space-y-0.5 overflow-auto">
            {assets.not_offered.map((n) => (
              <li key={n.symbol}>
                <code>{n.symbol}</code> {n.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* Whether the terminal behind a key is connected, in one line, and what its broker offers. */
export function MetaTraderKeyStatus({ credentialId }: { credentialId: string }) {
  const { t } = useTranslation('copy');
  const [status, setStatus] = useState<MetaTraderStatus | null>(null);
  useEffect(() => {
    let live = true;
    const load = async () => {
      const s = await brokerCredentialsService.metaTraderStatus(credentialId);
      if (live) setStatus(s);
    };
    void load();
    const id = setInterval(load, 15000);
    return () => {
      live = false;
      clearInterval(id);
    };
  }, [credentialId]);
  if (!status) return null;
  if (!status.connected) return <p className="text-xs text-muted-foreground">{t('broker.mt.waiting')}</p>;
  if (!status.online) return <p className="text-xs text-destructive">{t('broker.mt.offline')}</p>;
  if (!status.algo_trading_on) return <p className="text-xs text-destructive">{t('broker.mt.algoOff')}</p>;
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">
        {t('broker.mt.connected', { login: status.login, server: status.server, kind: status.kind, mode: status.margin_mode })}
      </p>
      {status.assets && <MetaTraderAssets assets={status.assets} />}
    </div>
  );
}

export function MetaTraderPairing({ label, onCreated }: { label: string; onCreated?: () => void }) {
  const { t } = useTranslation('copy');
  const [pairing, setPairing] = useState<MetaTraderPairingInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<MetaTraderStatus | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async (id: number) => {
    setStatus(await brokerCredentialsService.metaTraderStatus(String(id)));
  }, []);

  useEffect(() => {
    if (!pairing) return;
    void poll(pairing.id);
    timer.current = setInterval(() => void poll(pairing.id), 3000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [pairing, poll]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const p = await brokerCredentialsService.pairMetaTrader(label.trim());
      setPairing(p);
      onCreated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="metatrader-pairing">
      <p className="text-sm">{t('broker.mt.intro')}</p>
      <p className="text-xs text-muted-foreground">{t('broker.mt.perpetual')}</p>
      {!pairing ? (
        <Button type="button" className="w-full" onClick={() => void create()} disabled={busy}>
          {busy ? t('broker.mt.creating') : t('broker.mt.create')}
        </Button>
      ) : (
        <div className="space-y-3">
          <Button type="button" variant="outline" size="sm" asChild>
            <a href={pairing.ea_url} download>
              <Download className="mr-1 h-4 w-4" />
              {t('broker.mt.download')}
            </a>
          </Button>
          <ol className="list-decimal space-y-1 ps-5 text-xs text-muted-foreground">
            <li>{t('broker.mt.step1')}</li>
            <li>{t('broker.mt.step2')}</li>
            <li>{t('broker.mt.step3')}</li>
            <li>{t('broker.mt.step4')}</li>
          </ol>
          <CopyField label={t('broker.mt.address')} value={pairing.server_url} />
          <CopyField label={t('broker.mt.token')} value={pairing.token} />
          <p className="text-xs" role="status">
            {!status?.connected
              ? t('broker.mt.waiting')
              : !status.algo_trading_on
                ? t('broker.mt.algoOff')
                : t('broker.mt.connected', { login: status.login, server: status.server, kind: status.kind, mode: status.margin_mode })}
          </p>
          <p className="text-xs text-muted-foreground">{t('broker.mt.keepOn')}</p>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
